import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import SuperAdminLayout from "@/components/admin/super-admin-layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Building,
  Users,
  CreditCard,
  Shield,
  Activity,
  Search,
  Calendar,
  Filter,
  FileDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface SystemActivity {
  id: number;
  title: string;
  details?: string;
  time: string;
  timestamp: string;
  type: string;
  userId?: number;
  tenantId?: number;
}

// Format relative time (e.g., "2 hours ago")
const formatRelativeTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) {
    return `${diffInSeconds} second${diffInSeconds !== 1 ? 's' : ''} ago`;
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  }
  
  const diffInMonths = Math.floor(diffInDays / 30);
  return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''} ago`;
};

// Format date for display
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ActivityPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activityType, setActivityType] = useState("all");
  const [timeRange, setTimeRange] = useState("all");
  
  // Get system activity
  const { data: activities, isLoading } = useQuery<SystemActivity[]>({
    queryKey: ["/api/admin/system/activity"],
    queryFn: async () => {
      try {
        const response = await apiRequest<SystemActivity[]>("/api/admin/system/activity", {
          method: "GET",
        });
        return response;
      } catch (error) {
        console.error("Error fetching system activity:", error);
        throw error;
      }
    },
  });
  
  // Filter activities based on search query, activity type, and time range
  const filteredActivities = activities?.filter(activity => {
    // Filter by search query
    const matchesSearch = searchQuery === "" || 
      activity.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (activity.details && activity.details.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // Filter by activity type
    const matchesType = activityType === "all" || activity.type.includes(activityType);
    
    // Filter by time range
    let matchesTimeRange = true;
    if (timeRange !== "all") {
      const activityDate = new Date(activity.timestamp);
      const now = new Date();
      const diffInHours = (now.getTime() - activityDate.getTime()) / (1000 * 60 * 60);
      
      if (timeRange === "24h" && diffInHours > 24) {
        matchesTimeRange = false;
      } else if (timeRange === "7d" && diffInHours > 168) { // 7 * 24
        matchesTimeRange = false;
      } else if (timeRange === "30d" && diffInHours > 720) { // 30 * 24
        matchesTimeRange = false;
      }
    }
    
    return matchesSearch && matchesType && matchesTimeRange;
  });

  // Helper function to get the appropriate icon for an activity
  const getActivityIcon = (activity: SystemActivity) => {
    if (activity.type === 'tenant_created' || activity.type === 'tenant_verified') {
      return <Building className="h-5 w-5 text-blue-500" />;
    } else if (activity.type === 'user_created' || activity.type === 'user_updated') {
      return <Users className="h-5 w-5 text-purple-500" />;
    } else if (activity.type === 'subscription_created' || activity.type === 'payment_processed') {
      return <CreditCard className="h-5 w-5 text-emerald-500" />;
    } else if (activity.type === 'login_failed') {
      return <Shield className="h-5 w-5 text-rose-500" />;
    } else if (activity.type === 'login_success') {
      return <Shield className="h-5 w-5 text-emerald-500" />;
    } else {
      return <Activity className="h-5 w-5 text-slate-500" />;
    }
  };

  return (
    <SuperAdminLayout>
      <div>
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-4 md:mb-0">
              <h2 className="text-2xl font-bold text-slate-800">System Activity</h2>
              <p className="text-sm text-slate-500 mt-1">View and monitor all system events and activities</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                size="sm" 
                variant="outline"
                className="flex items-center"
              >
                <FileDown className="h-4 w-4 mr-2" />
                Export Activity
              </Button>
            </div>
          </div>
        </div>
        
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search activities..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <div>
                <Select value={activityType} onValueChange={setActivityType}>
                  <SelectTrigger>
                    <div className="flex items-center">
                      <Filter className="h-4 w-4 mr-2 text-slate-400" />
                      <SelectValue placeholder="Filter by type" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="tenant">Tenant Activity</SelectItem>
                    <SelectItem value="user">User Activity</SelectItem>
                    <SelectItem value="subscription">Subscription Activity</SelectItem>
                    <SelectItem value="login">Login Activity</SelectItem>
                    <SelectItem value="system">System Activity</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Select value={timeRange} onValueChange={setTimeRange}>
                  <SelectTrigger>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                      <SelectValue placeholder="Time range" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="24h">Last 24 Hours</SelectItem>
                    <SelectItem value="7d">Last 7 Days</SelectItem>
                    <SelectItem value="30d">Last 30 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button onClick={() => {
                setSearchQuery("");
                setActivityType("all");
                setTimeRange("all");
              }}>
                Reset Filters
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Activity List */}
        <Card>
          <CardHeader>
            <CardTitle>Activity Log</CardTitle>
            <CardDescription>
              {filteredActivities ? 
                `Showing ${filteredActivities.length} of ${activities?.length || 0} activities` : 
                "Loading activities..."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center my-8">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
              </div>
            ) : filteredActivities && filteredActivities.length > 0 ? (
              <div className="space-y-4">
                {filteredActivities.map(activity => (
                  <div 
                    key={activity.id} 
                    className="flex items-start p-4 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex-shrink-0 mr-4 p-2 rounded-full bg-slate-100">
                      {getActivityIcon(activity)}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col md:flex-row md:justify-between">
                        <h4 className="text-sm font-medium text-slate-800">{activity.title}</h4>
                        <span className="text-xs text-slate-500">{formatDate(activity.timestamp)}</span>
                      </div>
                      {activity.details && (
                        <p className="text-sm text-slate-600 mt-1">{activity.details}</p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-slate-100 text-slate-800">
                          {activity.type}
                        </span>
                        {activity.userId && (
                          <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800">
                            User ID: {activity.userId}
                          </span>
                        )}
                        {activity.tenantId && (
                          <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800">
                            Tenant ID: {activity.tenantId}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Activity className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <h3 className="text-lg font-medium text-slate-800 mb-1">No activities found</h3>
                <p className="text-slate-500">Try adjusting your filters or search query</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </SuperAdminLayout>
  );
};

export default ActivityPage;